import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Badge, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FiSearch, FiFilter, FiPlus, FiUsers, FiTrash2, FiPhone, FiMail, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';

export default function Users() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const [users, setUsers] = useState({ data: [], current_page: 1, last_page: 1, total: 0 });
    const [searchString, setSearchString] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const loadUsers = useCallback((page = 1, search = searchString) => {
        setIsLoading(true);
        setError(null);

        tre.api('GET', '/companies/' + context.idCompany + '/users', {
            context: context,
            params: {
                page: page,
                name: search,
                role: roleFilter,
                rows: 15,
                trash: showDeleted
            },
            success: (r) => {
                setUsers(r);
                setCurrentPage(page);
                setIsLoading(false);
            },
            error: (err) => {
                setError(err.error || 'Error al cargar los usuarios');
                setIsLoading(false);
            }
        });
    }, [context, showDeleted, roleFilter, searchString]);

    useEffect(() => {
        if (context && context.idCompany) {
            loadUsers(1, searchString);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context, showDeleted, roleFilter]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (context && context.idCompany) {
                loadUsers(1, searchString);
            }
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchString]);

    const onSelectUser = (user) => {
        if (user.deleted) {
            navigate('/user/' + user.id + '/deleted');
        } else {
            navigate('/user/' + user.id);
        }
    };

    const getRoleBadge = (role) => {
        const styles = {
            ADMIN: { bg: 'primary', label: 'Admin' },
            DRIVER: { bg: 'success', label: 'Conductor' },
            USER: { bg: 'secondary', label: 'Usuario' }
        };
        const style = styles[role] || styles.USER;
        return (
            <Badge bg={style.bg} style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}>
                {style.label}
            </Badge>
        );
    };

    // Pagination
    const totalPages = users.last_page || 1;
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            loadUsers(page, searchString);
        }
    };

    // -----| Render |-----
    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-1" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiUsers className="me-2" style={{ color: '#1a73e8' }} />
                        Usuarios
                    </h4>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                        {users.total || 0} usuarios en total
                    </p>
                </div>
                <Button variant="primary" onClick={() => navigate('/user/create')}>
                    <FiPlus className="me-2" />
                    Nuevo Usuario
                </Button>
            </div>

            {/* Filters - Inline style */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                {/* Search */}
                <div className="position-relative" style={{ minWidth: '280px', flex: 1, maxWidth: '400px' }}>
                    <FiSearch
                        className="position-absolute"
                        style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#5f6368' }}
                    />
                    <Form.Control
                        type="text"
                        placeholder="Buscar por nombre, login o DNI..."
                        value={searchString}
                        onChange={(e) => setSearchString(e.target.value)}
                        style={{ paddingLeft: '38px', backgroundColor: 'white' }}
                    />
                </div>

                {/* Role filter pills */}
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted me-1" style={{ fontSize: '14px' }}>Rol:</span>
                    <Button
                        size="sm"
                        variant={roleFilter === '' ? 'primary' : 'light'}
                        onClick={() => setRoleFilter('')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Todos
                    </Button>
                    <Button
                        size="sm"
                        variant={roleFilter === 'ADMIN' ? 'primary' : 'light'}
                        onClick={() => setRoleFilter('ADMIN')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Admin
                    </Button>
                    <Button
                        size="sm"
                        variant={roleFilter === 'DRIVER' ? 'success' : 'light'}
                        onClick={() => setRoleFilter('DRIVER')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Conductor
                    </Button>
                    <Button
                        size="sm"
                        variant={roleFilter === 'USER' ? 'secondary' : 'light'}
                        onClick={() => setRoleFilter('USER')}
                        style={{ borderRadius: '20px', padding: '6px 16px' }}
                    >
                        Usuario
                    </Button>
                </div>

                {/* Deleted toggle */}
                <Button
                    size="sm"
                    variant={showDeleted ? 'warning' : 'light'}
                    onClick={() => setShowDeleted(!showDeleted)}
                    style={{ borderRadius: '20px', padding: '6px 16px' }}
                >
                    <FiTrash2 className="me-1" />
                    {showDeleted ? 'Eliminados' : 'Ver eliminados'}
                </Button>

                {/* Clear filters */}
                {(searchString || roleFilter || showDeleted) && (
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => { setSearchString(''); setRoleFilter(''); setShowDeleted(false); }}
                        style={{ color: '#ea4335', textDecoration: 'none' }}
                    >
                        <FiFilter className="me-1" /> Limpiar
                    </Button>
                )}
            </div>

            {/* Deleted warning */}
            {showDeleted && (
                <Alert variant="warning" className="mb-4" style={{ borderRadius: '8px' }}>
                    <FiTrash2 className="me-2" />
                    Mostrando usuarios eliminados
                </Alert>
            )}

            {/* Table Card */}
            <Card>
                {/* Top Pagination Info */}
                {!isLoading && users.data && users.data.length > 0 && (
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                <strong style={{ color: '#202124' }}>{users.total || 0}</strong> registros
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Mostrando <strong style={{ color: '#202124' }}>{users.from || 0}</strong> - <strong style={{ color: '#202124' }}>{users.to || 0}</strong>
                            </span>
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                    Primera
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                    <FiChevronLeft className="me-1" /> Anterior
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                    Siguiente <FiChevronRight className="ms-1" />
                                </Button>
                                <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                    Última
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                )}

                <Card.Body className="p-0">
                    {error ? (
                        <Alert variant="danger" className="m-4" style={{ borderRadius: '8px' }}>
                            <p className="mb-2">{error}</p>
                            <Button variant="outline-danger" size="sm" onClick={() => loadUsers()}>
                                Reintentar
                            </Button>
                        </Alert>
                    ) : isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" style={{ color: '#1a73e8' }} />
                            <p className="mt-3 text-muted">Cargando usuarios...</p>
                        </div>
                    ) : users.data && users.data.length > 0 ? (
                        <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                <tr>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>USUARIO</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>LOGIN</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>ROL</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>IDA</th>
                                    <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>VUELTA</th>
                                    <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONTACTO</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.data.map((user) => (
                                    <tr
                                        key={user.id}
                                        onClick={() => onSelectUser(user)}
                                        style={{ cursor: 'pointer', opacity: user.deleted ? 0.6 : 1 }}
                                    >
                                        <td className="py-3 px-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: user.deleted ? '#fce8e6' : '#e8f0fe',
                                                        color: user.deleted ? '#ea4335' : '#1a73e8',
                                                        fontSize: '14px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {(user.name || '?').charAt(0).toUpperCase()}
                                                </div>
                                                <span style={{
                                                    color: '#202124',
                                                    fontWeight: 500,
                                                    textDecoration: user.deleted ? 'line-through' : 'none'
                                                }}>
                                                    {user.surname ? `${user.surname}, ${user.name}` : user.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3">
                                            <code style={{
                                                backgroundColor: '#f1f3f4',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}>
                                                {user.login}
                                            </code>
                                        </td>
                                        <td className="py-3">
                                            {getRoleBadge(user.role)}
                                        </td>
                                        <td className="py-3">
                                            {user.go ? (
                                                <span style={{
                                                    backgroundColor: '#e6f4ea',
                                                    color: '#34a853',
                                                    padding: '4px 10px',
                                                    borderRadius: '20px',
                                                    fontSize: '13px'
                                                }}>
                                                    {user.go.name}
                                                </span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="py-3">
                                            {user.come ? (
                                                <span style={{
                                                    backgroundColor: '#e8f0fe',
                                                    color: '#1a73e8',
                                                    padding: '4px 10px',
                                                    borderRadius: '20px',
                                                    fontSize: '13px'
                                                }}>
                                                    {user.come.name}
                                                </span>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            <div className="d-flex flex-column gap-1">
                                                {user.mobile && (
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: '13px' }}>
                                                        <FiPhone className="me-1" size={12} />
                                                        {user.mobile}
                                                    </span>
                                                )}
                                                {user.email && (
                                                    <span className="d-flex align-items-center text-muted" style={{ fontSize: '13px' }}>
                                                        <FiMail className="me-1" size={12} />
                                                        {user.email}
                                                    </span>
                                                )}
                                                {!user.mobile && !user.email && <span className="text-muted">-</span>}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="text-center py-5">
                            <FiUsers size={48} className="mb-3" style={{ color: '#dadce0' }} />
                            <p className="text-muted">No se encontraron usuarios</p>
                        </div>
                    )}
                </Card.Body>

                {/* Bottom Pagination */}
                {users.data && users.data.length > 0 && totalPages > 1 && (
                    <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center py-3 gap-3" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="d-flex align-items-center gap-4">
                            <span style={{ fontSize: '14px', color: '#5f6368' }}>
                                Página <strong style={{ color: '#202124' }}>{currentPage}</strong> de <strong style={{ color: '#202124' }}>{totalPages}</strong>
                            </span>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(1)}>
                                Primera
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>
                                <FiChevronLeft className="me-1" /> Anterior
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(currentPage + 1)}>
                                Siguiente <FiChevronRight className="ms-1" />
                            </Button>
                            <Button variant="outline-secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => goToPage(totalPages)}>
                                Última
                            </Button>
                        </div>
                    </Card.Footer>
                )}
            </Card>
        </div>
    );
}
